import { PassengerTypeCode } from '@models/common/shared-enums.model';
import { getResource } from '@utils/resource-translator.util';
interface SsrPassengerSource {
  resPaxId: number;
  passengerType: number;
  travelsWithPassengerId?: unknown;
  isPrimaryPassenger?: boolean;
}

interface SsrModalPassengerLike {
  id: string;
  type?: string;
}

const INFANT_PASSENGER_TYPE = Number(PassengerTypeCode.Infant);
const ADULT_PASSENGER_TYPE = Number(PassengerTypeCode.Adult);
const CHILD_PASSENGER_TYPE = Number(PassengerTypeCode.Child);

const resolveTypeLabel = (key: string, fallback: string): string => {
  const translated = getResource(key, undefined, fallback);
  return translated && translated !== key ? translated : fallback;
};

const resolveAdultTypeLabel = (): string => resolveTypeLabel('Adult.Text', 'Adult');
const resolveChildTypeLabel = (): string => resolveTypeLabel('Child.Text', 'Child');
const resolveInfantTypeLabel = (): string => resolveTypeLabel('Infant.Text', 'Infant');

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

export const resolvePassengerTypeLabel = (passengerType: number): string => {
  const normalizedPassengerType = Number(passengerType);

  if (normalizedPassengerType === ADULT_PASSENGER_TYPE) {
    return resolveAdultTypeLabel();
  }

  if (normalizedPassengerType === CHILD_PASSENGER_TYPE) {
    return resolveChildTypeLabel();
  }

  if (normalizedPassengerType === INFANT_PASSENGER_TYPE) {
    return resolveInfantTypeLabel();
  }

  return '';
};

export const isInfantPassengerTypeLabel = (type?: string): boolean => {
  const normalizedType = (type ?? '').trim().toLowerCase();
  const normalizedInfantLabel = resolveInfantTypeLabel().trim().toLowerCase();

  return (
    normalizedType === normalizedInfantLabel ||
    normalizedType === 'infant' ||
    normalizedType === 'infant.text'
  );
};

export const formatPassengerDisplayName = (
  title?: string,
  firstName?: string,
  lastName?: string,
): string => {
  return `${title ?? ''} ${firstName ?? ''} ${lastName ?? ''}`.replace(/\s+/g, ' ').trim();
};

export const findNextSelectablePassengerId = (
  passengers: SsrModalPassengerLike[],
  selectedPassengerId: string,
): string | undefined => {
  const currentPassengerIndex = passengers.findIndex(
    (passenger) => passenger.id === selectedPassengerId,
  );

  if (currentPassengerIndex < 0) {
    return undefined;
  }

  for (let index = currentPassengerIndex + 1; index < passengers.length; index += 1) {
    if (!isInfantPassengerTypeLabel(passengers[index]?.type)) {
      return passengers[index]?.id;
    }
  }

  return undefined;
};

export const resolveDefaultSelectablePassengerId = (
  passengers: SsrModalPassengerLike[],
): string => {
  return passengers.find((passenger) => !isInfantPassengerTypeLabel(passenger.type))?.id ?? '';
};

interface PassengerGroups<T> {
  infantByAdultId: Map<number, T[]>;
  unattachedInfants: T[];
  adults: T[];
  children: T[];
  primaryPassenger?: T;
}

const categorizePassengers = <T extends SsrPassengerSource>(
  passengers: T[],
): PassengerGroups<T> => {
  const infantByAdultId = new Map<number, T[]>();
  const unattachedInfants: T[] = [];
  const adults: T[] = [];
  const children: T[] = [];
  let primaryPassenger: T | undefined;

  passengers.forEach((passenger) => {
    if (passenger.passengerType === INFANT_PASSENGER_TYPE) {
      const assignedAdultId = toNumberOrUndefined(passenger.travelsWithPassengerId);
      if (assignedAdultId === undefined) {
        unattachedInfants.push(passenger);
        return;
      }

      const current = infantByAdultId.get(assignedAdultId) ?? [];
      current.push(passenger);
      infantByAdultId.set(assignedAdultId, current);
      return;
    }

    if (passenger.passengerType === ADULT_PASSENGER_TYPE) {
      if (passenger.isPrimaryPassenger) {
        primaryPassenger = passenger;
      } else {
        adults.push(passenger);
      }
      return;
    }

    if (passenger.passengerType === CHILD_PASSENGER_TYPE) {
      children.push(passenger);
      return;
    }

    // Fallback for any other passenger type
    if (passenger.isPrimaryPassenger) {
      primaryPassenger = passenger;
    } else {
      adults.push(passenger);
    }
  });

  return { infantByAdultId, unattachedInfants, adults, children, primaryPassenger };
};

const addPassengerWithInfants = <T>(
  ordered: T[],
  passenger: T & { resPaxId: number },
  infantByAdultId: Map<number, T[]>,
): void => {
  ordered.push(passenger);
  const assignedInfants = infantByAdultId.get(passenger.resPaxId) ?? [];
  assignedInfants.forEach((infant) => ordered.push(infant));
  infantByAdultId.delete(passenger.resPaxId);
};

export const sortPassengersWithAssignedInfants = <T extends SsrPassengerSource>(
  passengers: T[],
): T[] => {
  if (passengers.length <= 1) {
    return passengers;
  }

  const { infantByAdultId, unattachedInfants, adults, children, primaryPassenger } =
    categorizePassengers(passengers);

  const ordered: T[] = [];

  // Add primary passenger first with their assigned infants
  if (primaryPassenger) {
    addPassengerWithInfants(ordered, primaryPassenger, infantByAdultId);
  }

  // Add remaining adults with their assigned infants
  adults.forEach((passenger) => {
    addPassengerWithInfants(ordered, passenger, infantByAdultId);
  });

  // Add children
  children.forEach((passenger) => {
    ordered.push(passenger);
  });

  // Add orphaned infants (assigned to adults not in this list or unattached)
  infantByAdultId.forEach((infants) => {
    infants.forEach((infant) => ordered.push(infant));
  });
  unattachedInfants.forEach((infant) => ordered.push(infant));

  return ordered;
};

export const countNonInfantPassengers = (passengers: SsrModalPassengerLike[]): number => {
  return passengers.filter((passenger) => !isInfantPassengerTypeLabel(passenger.type)).length;
};
